import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { WizardContext } from '@/pages/precios/components/SalesAIChat';

/**
 * Contexto neutral con el que Alejandro arranca en páginas sin cotizador
 * (Inicio, Paquetes, Productos, Muestras, Ayuda...).
 */
export const EMPTY_ADVISOR_CONTEXT: WizardContext = {
  step: 1,
  eventType: '',
  eventCity: '',
  coverageId: null,
  hasPreevento: false,
  extraIds: [],
  finalTotal: 0,
  cargoDistancia: 0,
};

interface SalesAdvisorState {
  context: WizardContext;
  openRequest: number;
  openAdvisor: () => void;
  setContext: (context: WizardContext) => void;
}

const SalesAdvisorContext = createContext<SalesAdvisorState | null>(null);

/**
 * Provee el estado del asesor. Se monta UNA vez en el layout público,
 * que a su vez renderiza UNA sola instancia visual de Alejandro.
 */
export function SalesAdvisorProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<WizardContext>(EMPTY_ADVISOR_CONTEXT);
  const [openRequest, setOpenRequest] = useState(0);

  const openAdvisor = useCallback(() => setOpenRequest((n) => n + 1), []);

  const value = useMemo<SalesAdvisorState>(
    () => ({ context, openRequest, openAdvisor, setContext }),
    [context, openRequest, openAdvisor],
  );

  return <SalesAdvisorContext.Provider value={value}>{children}</SalesAdvisorContext.Provider>;
}

/** Estado completo, para el componente que monta el chat. */
export function useSalesAdvisorState(): SalesAdvisorState {
  const value = useContext(SalesAdvisorContext);
  if (value) return value;
  return {
    context: EMPTY_ADVISOR_CONTEXT,
    openRequest: 0,
    openAdvisor: () => {},
    setContext: () => {},
  };
}

/** Control externo (abrir el chat desde la hoja "Más" en móvil). */
export function useSalesAdvisorControls() {
  const { openAdvisor } = useSalesAdvisorState();
  return { openAdvisor };
}

/**
 * Las páginas publican el contexto de su embudo sin montar otro chat.
 * La clave serializada evita bucles de render cuando el objeto cambia de identidad.
 */
export function useSalesAdvisorPublisher(context: WizardContext) {
  const { setContext } = useSalesAdvisorState();
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    setContext(JSON.parse(contextKey) as WizardContext);
  }, [contextKey, setContext]);
}